import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import _, { isEmpty } from "lodash";
import { Form, TreeSelect } from 'antd';
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes } from 'utils/formUtils';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { uniqBy } from 'utils/lodashUtils';

const resourceTypes = [
  { title: 'Bucket', value: 'BUCKET' },
  { title: 'Container', value: 'CONTAINER' },
  { title: 'Container Image', value: 'CONTAINER_IMAGE' },
  { title: 'Application Endpoint', value: 'ENDPOINT' },
  { title: 'Repository Branch', value: 'REPOSITORY_BRANCH' },
  { title: 'Serverless', value: 'SERVERLESS' },
  { title: 'Virtual Machine', value: 'VIRTUAL_MACHINE' },
  { title: 'Virtual Machine Image', value: 'VIRTUAL_MACHINE_IMAGE' },
];

const resourceTypeValues = new Set(resourceTypes.map(r => r.value));

export const getAssetTypes = (featureFlags = {}) => [
  {
    title: 'Risk Issues',
    value: 'RISK_ISSUES_GROUP',
    selectable: false,
    checkable: false,
    children: [
      { title: 'Issues', value: 'ISSUES' },
    ],
  },
  {
    title: 'Security Posture',
    value: 'SECURITY_POSTURE_GROUP',
    selectable: false,
    checkable: false,
    children: [
      {
        title: 'Vulnerability Findings',
        value: 'VULNERABILITY_FINDINGS',
        selectable: false,
        checkable: false,
        children: [
          {
            title: 'Resource Type',
            value: 'RESOURCE_TYPE_GROUP',
            selectable: false,
            checkable: false,
            children: resourceTypes,
          },
        ],
      },
      { title: 'Cloud Configuration Findings', value: 'CLOUD_CONFIGURATION' },
      { title: 'Data Findings', value: 'DATA_FINDINGS' },
      { title: 'Network Exposure Findings', value: 'NETWORK_EXPOSURE' },
      ...(featureFlags.WIZ_SECRET_FINDINGS_ENABLE ? [{ title: 'Secret Findings', value: 'SECRET_FINDINGS' }] : []),
    ],
  },
];

// Bucket (S3) path asset types — Secret Findings here is intentionally NOT gated
// by WIZ_SECRET_FINDINGS_ENABLE. The bucket path already supports Secret Findings
// and should remain unaffected by the API FF.
export const wizBucketAssetTypes = [
  {
    title: 'Security Posture',
    value: 'SECURITY_POSTURE_BUCKET_GROUP',
    selectable: false,
    checkable: false,
    children: [
      { title: "Host Configuration Findings", value: "HOST_CONFIGURATION_FINDINGS" },
      { title: "Secret Findings", value: "SECRET_FINDINGS" },
      { title: "Attack Surface Findings", value: "EXTERNAL_ATTACK_SURFACE" },
      { title: "Cloud Configuration Findings", value: "CLOUD_CONFIGURATION_FINDINGS" },
    ],
  }
];

// Legacy flat arrays (FF off) — labels differ from new TreeSelect but API values are identical.
export const getLegacyAssetTypes = (featureFlags = {}) => [
  { label: 'VM', value: 'VIRTUAL_MACHINE' },
  { label: 'Issues', value: 'ISSUES' },
  { label: 'Container', value: 'CONTAINER' },
  { label: 'Container Image', value: 'CONTAINER_IMAGE' },
  { label: 'Serverless', value: 'SERVERLESS' },
  { label: 'Cloud Configurations', value: 'CLOUD_CONFIGURATION' },
  { label: 'Data Findings', value: 'DATA_FINDINGS' },
  { label: 'Network Exposure', value: 'NETWORK_EXPOSURE' },
  { label: 'Application Endpoint Findings', value: 'ENDPOINT' },
  { label: 'Repository Branch Findings', value: 'REPOSITORY_BRANCH' },
  { label: 'Bucket', value: 'BUCKET' },
  { label: 'Virtual Machine Image', value: 'VIRTUAL_MACHINE_IMAGE' },
  ...(featureFlags.WIZ_SECRET_FINDINGS_ENABLE ? [{ label: 'Secret Findings', value: 'SECRET_FINDINGS' }] : []),
];

// Legacy flat arrays (FF off) — labels differ from new TreeSelect but API values are identical.
// Note: legacyBucketAssetTypes is intentionally NOT filtered by WIZ_SECRET_FINDINGS_ENABLE —
// bucket path Secret Findings support is pre-existing and independent of the API FF.
export const legacyBucketAssetTypes = [
  { label: "Host Configuration Findings", value: "HOST_CONFIGURATION_FINDINGS" },
  { label: "Secret Findings", value: "SECRET_FINDINGS" },
  { label: "External Attack Surface", value: "EXTERNAL_ATTACK_SURFACE" },
  { label: "Cloud Configuration Findings", value: "CLOUD_CONFIGURATION_FINDINGS" },
];

const initialFormValues = [{
    product: undefined,
    subProduct: undefined,
    environment: undefined,
    assetType: undefined
  }];

const WizMappingForm = forwardRef(function WizMappingForm(props, ref){
    const [form] = Form.useForm();
    const featureFlags = useFeatureFlag();
    const isWizBucket = props.loginConfig?.bucketName;
    if(isWizBucket){
      initialFormValues[0].assetType = undefined;
    } else{
      initialFormValues[0].assetType = ['VIRTUAL_MACHINE', 'ISSUES'];
    }
    const [mappedProjectList] = useState(() => {
      // this state contains the already mapped application/site
      // This is stored because the project and version dropdowns are paginated
      // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
      if (props.record) {
        const { projectId, projectName } = props.record;
        return {
          projects: projectId ? [{ id: projectId, name: projectName }] : [],
        }
      }
      return { projects: [] }
    })

    const [options,setOptions] = useState(mappedProjectList.projects);
    
    const setOptionsFromPagination = (option) => {
        setOptions(prev => uniqBy([...prev,...option], 'id'));
    }

    const fetchProjects = useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,...params });
      promise.then((response) => {
        if(mappedProjectList['projects'].length > 0 && !isEmpty(response.projects)){
          response.projects = [...response.projects, ...mappedProjectList['projects']];
        }
        setOptionsFromPagination(response.projects)
      });
      return { promise, cancel };
    }, [props.toolName, mappedProjectList, props.loginConfigId]);

    const getExtraFieldsData = useCallback((config) => {
        const { projectId, assetType } = config;
        const projectName = options.find(project => project.id === projectId)?.name;
        let flatAssetTypes;
        if (featureFlags.WIZ_ASSET_TYPE_TREE_SELECT) {
          const wizBucketAssetTypeValue = wizBucketAssetTypes
              .flatMap(type => type.children || [type])
              .map(type => type.value);
          const nonLeafValues = new Set();
          const collectNonLeaf = (items) => {
            items.forEach(t => {
              if (t.children) {
                nonLeafValues.add(t.value);
                collectNonLeaf(t.children);
              }
            });
          };
          collectNonLeaf(getAssetTypes(featureFlags));
          flatAssetTypes = isWizBucket
            ? (isEmpty(assetType) ? wizBucketAssetTypeValue : assetType)
            : (isEmpty(assetType) ? initialFormValues[0].assetType : assetType.filter(v => !nonLeafValues.has(v)));
        } else {
          const wizBucketAssetTypeValue = legacyBucketAssetTypes.map(type => type.value);
          flatAssetTypes = isEmpty(assetType)
            ? (isWizBucket ? wizBucketAssetTypeValue : initialFormValues[0].assetType)
            // Bucket path returns assetType as-is — bucket Secret Findings is not gated by WIZ_SECRET_FINDINGS_ENABLE
            : assetType;
        }
        return ({
            projectId: isEmpty((projectId || "").trim()) ? undefined : projectId,
            projectName: projectName,
            assetType: flatAssetTypes.join()
    })
    }, [featureFlags, isWizBucket, options]);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { assetType } = config;
        return { assetType: assetType.split(',') };
      }, []);

    const extraFields = useMemo(() => [
      {
        name: "projectId",
        colSpan: 10,
        label: featureFlags.WIZ_PULL_ROOT_PROJECTS ? "Parent Project / Folder Name" :"Project Name",
        placeholder: "Leave blank to select all",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
            autoFocus: true,
            showValueOnload: false,
            api: fetchProjects,
            apiParams: { login_id: props.loginConfigId },
        }},
        ...(isWizBucket ? [
          featureFlags.WIZ_ASSET_TYPE_TREE_SELECT ? {
            name: 'assetType',
            colSpan: '14',
            label: 'Select Wiz Issues/Findings',
            help: 'If not specified, then defaults to All',
            render: (fieldProps) => (
              <TreeSelect
                {...fieldProps}
                treeData={wizBucketAssetTypes}
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                placeholder="Select Wiz Issues/Findings"
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                treeDefaultExpandAll
              />
            ),
          } : {
            name: 'assetType',
            colSpan: '10',
            placeholder: 'Asset Type',
            type: FieldTypes.MULTI_SELECT,
            label: 'Asset Type',
            help: 'If not specified, then defaults to All',
            options: legacyBucketAssetTypes,
          }
        ] : [
          featureFlags.WIZ_ASSET_TYPE_TREE_SELECT ? {
            name: 'assetType',
            colSpan: '14',
            label: 'Select Wiz Issues/Findings',
            help: 'If not specified, defaults to ingestion of Issues and Virtual Machine Vulnerability Findings.',
            render: (fieldProps) => (
              <TreeSelect
                {...fieldProps}
                treeData={getAssetTypes(featureFlags)}
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                placeholder="Select Wiz Issues/Findings"
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                treeDefaultExpandAll
                tagRender={({ label, value: val, closable, onClose }) => (
                  <span className="ant-select-selection-item" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span className="ant-select-selection-item-content">
                      {resourceTypeValues.has(val) ? `Vulnerability Findings → ${label}` : label}
                    </span>
                    {closable && <span className="ant-select-selection-item-remove" onClick={onClose}>×</span>}
                  </span>
                )}
              />
            ),
          } : {
            name: 'assetType',
            colSpan: '10',
            placeholder: 'Asset Type',
            type: FieldTypes.MULTI_SELECT,
            label: 'Asset Type',
            help: 'If not specified, defaults to VM and Issues.',
            options: getLegacyAssetTypes(featureFlags),
          }
        ])
    ], [featureFlags, fetchProjects, isWizBucket, props.loginConfigId])

    return (
        <GenericMappingForm 
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            extraFields={extraFields}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            formDefaultValues={initialFormValues}
            newRowDefaultValue={initialFormValues[0]}
            {...props}
        />
    )
});

export default WizMappingForm;